import * as React from 'react';
import {
  Header,
  Form,
  Dropdown,
  Button,
  Message,
  Table,
  Grid
} from 'semantic-ui-react';
import useCsrfFetchJson from './hooks/use_csrf_fetch';
import './mud.css';

const statKeys = ['perm_str', 'perm_int', 'perm_wis', 'perm_dex', 'perm_con', 'perm_cha'];
const statLabels = {
  perm_str: 'STR',
  perm_int: 'INT',
  perm_wis: 'WIS',
  perm_dex: 'DEX',
  perm_con: 'CON',
  perm_cha: 'CHA'
};
const statMin = 8;
const statMax = 15;
const pointBudget = 27;

const BASIC_CLASS_NAMES = [
  'Combat',
  'Piloting',
  'Engineering',
  'Smuggling',
  'Diplomacy',
  'Leadership',
  'Slicer',
  'Bounty Hunting',
  'Hunting'
];

const FORCE_CLASS_NAMES = [
  'Jedi Guardian',
  'Jedi Consular',
  'Jedi Sentinel',
  'Sith Warrior',
  'Sith Inquisitor',
  'Dathomirian Witch'
];

const emptyStats = statKeys.reduce((memo, key) => {
  memo[key] = statMin;
  return memo;
}, {});

const CharacterNew = () => {
  const fetchJson = useCsrfFetchJson();
  const [options, setOptions] = React.useState(null);
  const [loadingOptions, setLoadingOptions] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState(null);
  const [maxLevels, setMaxLevels] = React.useState([]);
  const [maxLevelsLoading, setMaxLevelsLoading] = React.useState(false);

  const [form, setForm] = React.useState({
    name: '',
    last_name: '',
    password: '',
    password_confirmation: '',
    sex: '',
    main_ability_id: '',
    race_id: '',
    pheight: '',
    build: '',
    alignment: '',
    description: '',
    image_url: ''
  });

  const [stats, setStats] = React.useState(emptyStats);
  const [activeStep, setActiveStep] = React.useState(0);

  React.useEffect(() => {
    let mounted = true;
    const loadOptions = async () => {
      try {
        const data = await fetchJson('/player/characters/chargen_options');
        if (mounted) {
          setOptions(data);
          setLoadingOptions(false);
        }
      } catch (err) {
        if (mounted) {
          setError(err.message);
          setLoadingOptions(false);
        }
      }
    };
    loadOptions();
    return () => { mounted = false; };
  }, [fetchJson]);

  React.useEffect(() => {
    const shouldFetch = form.main_ability_id && form.race_id;
    if (!shouldFetch) {
      setMaxLevels([]);
      return;
    }
    let active = true;
    const loadLevels = async () => {
      try {
        setMaxLevelsLoading(true);
        const data = await fetchJson('/player/characters/chargen_max_levels', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            race_id: form.race_id,
            main_ability_id: form.main_ability_id,
            ...stats
          })
        });
        if (active) {
          setMaxLevels(data.levels || []);
        }
      } catch (err) {
        if (active) {
          setMaxLevels([]);
        }
      } finally {
        if (active) {
          setMaxLevelsLoading(false);
        }
      }
    };
    loadLevels();
    return () => { active = false; };
  }, [fetchJson, form.main_ability_id, form.race_id, stats]);

  const handleChange = (field) => (_, data) => {
    setForm((prev) => ({ ...prev, [field]: data.value }));
  };

  const handleInputChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const pointsSpent = statKeys.reduce((sum, key) => sum + (stats[key] - statMin), 0);
  const pointsRemaining = pointBudget - pointsSpent;

  const adjustStat = (key, delta) => {
    setStats((prev) => {
      const nextValue = prev[key] + delta;
      if (nextValue < statMin || nextValue > statMax) return prev;
      const nextSpent = pointsSpent + delta;
      if (nextSpent > pointBudget) return prev;
      return { ...prev, [key]: nextValue };
    });
  };

  const requiredMissing = () => {
    const required = [
      'name',
      'password',
      'password_confirmation',
      'sex',
      'main_ability_id',
      'race_id',
      'pheight',
      'build',
      'alignment'
    ];
    const missing = required.filter((field) => form[field] === '' || form[field] === null || form[field] === undefined);
      if (pointsRemaining !== 0) missing.push('stats');
    return missing;
  };

  const stepMissing = () => {
    if (activeStep === 1) {
      const required = ['race_id'];
      return required.filter((field) => form[field] === '' || form[field] === null || form[field] === undefined);
    }
    if (activeStep === 2) {
      const required = ['main_ability_id'];
      return required.filter((field) => form[field] === '' || form[field] === null || form[field] === undefined);
    }
    if (activeStep === 3) {
      const required = ['name', 'password', 'password_confirmation', 'sex', 'pheight', 'build', 'alignment'];
      return required.filter((field) => form[field] === '' || form[field] === null || form[field] === undefined);
    }
    if (activeStep === 4) {
      if (pointsRemaining !== 0) return ['stats'];
      return [];
    }
    return [];
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);
    const missing = requiredMissing();
    if (missing.length) {
      setError(`Missing required fields: ${missing.join(', ')}`);
      return;
    }
    if (form.password !== form.password_confirmation) {
      setError('Password confirmation does not match.');
      return;
    }
    if (form.password.length < 5) {
      setError('Password must be at least five characters long.');
      return;
    }
    if (form.password.includes('~')) {
      setError('Password cannot include ~.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        character: {
          ...form,
          ...stats
        }
      };
      delete payload.character.description;
      delete payload.character.image_url;
      const data = await fetchJson('/player/characters/chargen', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      setSuccess(`Character ${data.name} created.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const toDropdown = (items) => items.map((item) => ({
    key: item.value,
    value: item.value,
    text: item.label
  }));

  const groupedClassOptions = () => {
    const basics = options?.classes?.filter((item) => BASIC_CLASS_NAMES.includes(item.label)) || [];
    const forces = options?.classes?.filter((item) => FORCE_CLASS_NAMES.includes(item.label)) || [];
    const other = options?.classes?.filter(
      (item) => !BASIC_CLASS_NAMES.includes(item.label) && !FORCE_CLASS_NAMES.includes(item.label)
    ) || [];

    const formatted = [];
    if (basics.length) {
      formatted.push({ key: 'basic-header', text: 'Basic Classes', value: '', disabled: true, header: true });
      formatted.push(...toDropdown(basics));
    }
    if (other.length) {
      if (formatted.length) {
        formatted.push({ key: 'basic-divider', text: '', value: '', disabled: true, divider: true });
      }
      formatted.push({ key: 'other-header', text: 'Other Classes', value: '', disabled: true, header: true });
      formatted.push(...toDropdown(other));
    }
    if (forces.length) {
      if (formatted.length) {
        formatted.push({ key: 'force-divider', text: '', value: '', disabled: true, divider: true });
      }
      formatted.push({ key: 'force-header', text: 'Force Classes', value: '', disabled: true, header: true });
      formatted.push(...toDropdown(forces));
    }
    return formatted;
  };

  const steps = [
    { key: 'welcome', title: 'Welcome', description: 'Important notes' },
    { key: 'race', title: 'Species', description: 'Pick + review stats' },
    { key: 'class', title: 'Class', description: 'Pick your main ability' },
    { key: 'basics', title: 'Basics', description: 'Identity + choices' },
    { key: 'stats', title: 'Stats', description: 'Roll + confirm' }
  ];

  const canGoNext = stepMissing().length === 0;

  return (
    <div className="lots-shell">
      <div className="lots-container">
        <div className="lots-hero">
          <div className="portal-kicker">Character Creation</div>
          <h1 className="lots-hero__title">Forge a new legacy</h1>
          <p className="lots-hero__subtitle">
            Build your character step-by-step, then jump into the world via the Play Online client or a dedicated MUD
            client.
          </p>
        </div>
        <div className="portal-layout">
          <div className="portal-nav">
            <div className="portal-kicker">Steps</div>
            <ul className="lots-step-list">
              {steps.map((step, index) => (
                <li
                  key={step.key}
                  className={[
                    'lots-step',
                    index === activeStep ? 'active' : '',
                    index < activeStep ? 'completed' : ''
                  ].join(' ').trim()}
                  onClick={() => setActiveStep(index)}
                >
                  {step.title}
                  <div className="portal-note">{step.description}</div>
                </li>
              ))}
            </ul>
            <div className="portal-note" style={{ marginTop: '1rem' }}>
              Points must total {pointBudget}. Stats are final after creation.
            </div>
          </div>
          <div className="portal-main">
            {loadingOptions && <Message info>Loading options...</Message>}
            {error && <Message negative>{error}</Message>}
            {success && (
              <Message positive>
                <Message.Header>Success</Message.Header>
                <p>{success}</p>
                <p><a href="/player">Go to Player Portal</a> to select your character.</p>
              </Message>
            )}
            {options && (
              <div className="portal-section">
                <Form>
            {activeStep === 0 && (
              <>
                <Header as="h3">Welcome</Header>
                <div className="lots-card lots-card--accent">
                  <h4>Before you begin</h4>
                  <p>
                    The web experience is still growing. For the best stability, use a dedicated MUD client once your
                    character is created.
                  </p>
                  <p>
                    Recommended clients:{' '}
                    <a href="https://www.mudlet.org/" target="_blank" rel="noreferrer">Mudlet</a>
                    {' '}and{' '}
                    <a href="https://www.mushclient.com/" target="_blank" rel="noreferrer">MUSHclient</a>.
                  </p>
                </div>
                <div className="portal-cta-row">
                  <span className="portal-chip">Step {activeStep + 1} of {steps.length}</span>
                  <a className="lots-btn lots-btn--ghost" href="/how-to-play">How to Play</a>
                </div>
              </>
            )}

            {activeStep === 1 && (
              <>
                <Header as="h3">Race</Header>
                <Grid stackable columns={2}>
                  <Grid.Column width={10}>
                    <Form.Field required>
                      <label>Race</label>
                      <Dropdown
                        placeholder="Select race"
                        selection
                        options={toDropdown(options.races)}
                        value={form.race_id}
                        onChange={handleChange('race_id')}
                      />
                    </Form.Field>
                    {form.race_id && (
                      <Segment>
                        <Header as="h4">Racial Stats</Header>
                        <Table celled compact>
                          <Table.Header>
                            <Table.Row>
                              <Table.HeaderCell>STR+</Table.HeaderCell>
                              <Table.HeaderCell>DEX+</Table.HeaderCell>
                              <Table.HeaderCell>WIS+</Table.HeaderCell>
                              <Table.HeaderCell>INT+</Table.HeaderCell>
                              <Table.HeaderCell>CON+</Table.HeaderCell>
                              <Table.HeaderCell>CHA+</Table.HeaderCell>
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            <Table.Row>
                              {(() => {
                                const race = options.races.find((item) => item.value === form.race_id);
                                const stats = race?.stats || {};
                                return (
                                  <>
                                    <Table.Cell>{stats.str_plus ?? 0}</Table.Cell>
                                    <Table.Cell>{stats.dex_plus ?? 0}</Table.Cell>
                                    <Table.Cell>{stats.wis_plus ?? 0}</Table.Cell>
                                    <Table.Cell>{stats.int_plus ?? 0}</Table.Cell>
                                    <Table.Cell>{stats.con_plus ?? 0}</Table.Cell>
                                    <Table.Cell>{stats.cha_plus ?? 0}</Table.Cell>
                                  </>
                                );
                              })()}
                            </Table.Row>
                          </Table.Body>
                        </Table>
                        <Table celled compact>
                          <Table.Header>
                            <Table.Row>
                              <Table.HeaderCell>Luck+</Table.HeaderCell>
                              <Table.HeaderCell>Force+</Table.HeaderCell>
                              <Table.HeaderCell>Hit</Table.HeaderCell>
                              <Table.HeaderCell>Mana</Table.HeaderCell>
                              <Table.HeaderCell>Resist</Table.HeaderCell>
                              <Table.HeaderCell>Suscept</Table.HeaderCell>
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            <Table.Row>
                              {(() => {
                                const race = options.races.find((item) => item.value === form.race_id);
                                const stats = race?.stats || {};
                                return (
                                  <>
                                    <Table.Cell>{stats.lck_plus ?? 0}</Table.Cell>
                                    <Table.Cell>{stats.frc_plus ?? 0}</Table.Cell>
                                    <Table.Cell>{stats.hit ?? 0}</Table.Cell>
                                    <Table.Cell>{stats.mana ?? 0}</Table.Cell>
                                    <Table.Cell>{stats.resist ?? 0}</Table.Cell>
                                    <Table.Cell>{stats.suscept ?? 0}</Table.Cell>
                                  </>
                                );
                              })()}
                            </Table.Row>
                          </Table.Body>
                        </Table>
                        <Table celled compact>
                          <Table.Header>
                            <Table.Row>
                              <Table.HeaderCell>Combat</Table.HeaderCell>
                              <Table.HeaderCell>Piloting</Table.HeaderCell>
                              <Table.HeaderCell>Engineering</Table.HeaderCell>
                              <Table.HeaderCell>Hunting</Table.HeaderCell>
                              <Table.HeaderCell>Smuggling</Table.HeaderCell>
                              <Table.HeaderCell>Diplomacy</Table.HeaderCell>
                              <Table.HeaderCell>Leadership</Table.HeaderCell>
                              <Table.HeaderCell>Slicer</Table.HeaderCell>
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            <Table.Row>
                              {(() => {
                                const race = options.races.find((item) => item.value === form.race_id);
                                const stats = race?.stats || {};
                                return (
                                  <>
                                    <Table.Cell>{stats.combat_mod ?? 0}</Table.Cell>
                                    <Table.Cell>{stats.pilot_mod ?? 0}</Table.Cell>
                                    <Table.Cell>{stats.engi_mod ?? 0}</Table.Cell>
                                    <Table.Cell>{stats.hunting_mod ?? 0}</Table.Cell>
                                    <Table.Cell>{stats.smuggling_mod ?? 0}</Table.Cell>
                                    <Table.Cell>{stats.diplomacy_mod ?? 0}</Table.Cell>
                                    <Table.Cell>{stats.leadership_mod ?? 0}</Table.Cell>
                                    <Table.Cell>{stats.slicer_mod ?? 0}</Table.Cell>
                                  </>
                                );
                              })()}
                            </Table.Row>
                          </Table.Body>
                        </Table>
                      </Segment>
                    )}
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <div className="lots-card">
                      <Header as="h4">Race Details</Header>
                      <p>
                        Flavor, strengths, and cultural notes will appear here as we wire lore summaries.
                      </p>
                      <div style={{ border: '1px dashed #333', padding: '1rem', textAlign: 'center' }}>
                        Race image placeholder
                      </div>
                    </div>
                  </Grid.Column>
                </Grid>
              </>
            )}

            {activeStep === 2 && (
              <>
                <Header as="h3">Class</Header>
                <Grid stackable columns={2}>
                  <Grid.Column width={10}>
                    <Form.Field required>
                      <label>Class</label>
                      <Dropdown
                        placeholder="Select class"
                        selection
                        options={groupedClassOptions()}
                        value={form.main_ability_id}
                        onChange={handleChange('main_ability_id')}
                      />
                    </Form.Field>
                    {form.main_ability_id && (
                      <>
                        <Header as="h4">Max Levels by Class</Header>
                        {maxLevelsLoading && <Message info>Loading max levels...</Message>}
                        {!maxLevelsLoading && (
                          <Table celled compact>
                            <Table.Header>
                              <Table.Row>
                                <Table.HeaderCell>Class</Table.HeaderCell>
                                <Table.HeaderCell>Max Level</Table.HeaderCell>
                              </Table.Row>
                            </Table.Header>
                            <Table.Body>
                              {maxLevels.filter((row) => BASIC_CLASS_NAMES.includes(row.name)).length > 0 && (
                                <Table.Row>
                                  <Table.Cell colSpan="2"><strong>Basic Classes</strong></Table.Cell>
                                </Table.Row>
                              )}
                              {maxLevels
                                .filter((row) => BASIC_CLASS_NAMES.includes(row.name))
                                .map((row) => (
                                  <Table.Row key={row.id}>
                                    <Table.Cell>{row.name}</Table.Cell>
                                    <Table.Cell>{row.max_level}</Table.Cell>
                                  </Table.Row>
                                ))}
                              {maxLevels.filter((row) => FORCE_CLASS_NAMES.includes(row.name)).length > 0 && (
                                <Table.Row>
                                  <Table.Cell colSpan="2"><strong>Force Classes</strong></Table.Cell>
                                </Table.Row>
                              )}
                              {maxLevels
                                .filter((row) => FORCE_CLASS_NAMES.includes(row.name))
                                .map((row) => (
                                  <Table.Row key={row.id}>
                                    <Table.Cell>{row.name}</Table.Cell>
                                    <Table.Cell>{row.max_level}</Table.Cell>
                                  </Table.Row>
                                ))}
                              {maxLevels.filter(
                                (row) => !BASIC_CLASS_NAMES.includes(row.name) && !FORCE_CLASS_NAMES.includes(row.name)
                              ).length > 0 && (
                                <Table.Row>
                                  <Table.Cell colSpan="2"><strong>Other Classes</strong></Table.Cell>
                                </Table.Row>
                              )}
                              {maxLevels
                                .filter(
                                  (row) => !BASIC_CLASS_NAMES.includes(row.name) && !FORCE_CLASS_NAMES.includes(row.name)
                                )
                                .map((row) => (
                                  <Table.Row key={row.id}>
                                    <Table.Cell>{row.name}</Table.Cell>
                                    <Table.Cell>{row.max_level}</Table.Cell>
                                  </Table.Row>
                                ))}
                            </Table.Body>
                          </Table>
                        )}
                      </>
                    )}
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <div className="lots-card">
                      <Header as="h4">Class Details</Header>
                      <p>
                        Lore and class playstyle notes will appear here once descriptions are wired.
                      </p>
                      <div style={{ border: '1px dashed #333', padding: '1rem', textAlign: 'center' }}>
                        Class image placeholder
                      </div>
                    </div>
                  </Grid.Column>
                </Grid>
              </>
            )}

            {activeStep === 3 && (
              <>
                <Header as="h3">Identity</Header>
                <Form.Input
                  label="Name"
                  placeholder="Name"
                  value={form.name}
                  onChange={handleInputChange('name')}
                  required
                />
                <Form.Input
                  label="Last Name"
                  placeholder="Last name (optional)"
                  value={form.last_name}
                  onChange={handleInputChange('last_name')}
                />
                <Form.Input
                  label="Password"
                  placeholder="Password"
                  type="password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={handleInputChange('password')}
                  required
                />
                <Form.Input
                  label="Confirm Password"
                  placeholder="Confirm password"
                  type="password"
                  autoComplete="new-password"
                  value={form.password_confirmation}
                  onChange={handleInputChange('password_confirmation')}
                  required
                />
                <Form.TextArea
                  label="Description"
                  placeholder="Coming soon"
                  value={form.description}
                  onChange={handleInputChange('description')}
                  disabled
                />
                <Form.Input
                  label="Image"
                  placeholder="Coming soon"
                  value={form.image_url}
                  onChange={handleInputChange('image_url')}
                  disabled
                />

                <Header as="h3">Basics</Header>
                <Form.Field required>
                  <label>Sex</label>
                  <Dropdown
                    placeholder="Select sex"
                    selection
                    options={toDropdown(options.sexes)}
                    value={form.sex}
                    onChange={handleChange('sex')}
                  />
                </Form.Field>
                <Form.Group widths="equal">
                  <Form.Field required>
                    <label>Height</label>
                    <Dropdown
                      placeholder="Select height"
                      selection
                      options={toDropdown(options.heights)}
                      value={form.pheight}
                      onChange={handleChange('pheight')}
                    />
                  </Form.Field>
                  <Form.Field required>
                    <label>Build</label>
                    <Dropdown
                      placeholder="Select build"
                      selection
                      options={toDropdown(options.builds)}
                      value={form.build}
                      onChange={handleChange('build')}
                    />
                  </Form.Field>
                </Form.Group>
                <Form.Field required>
                  <label>Alignment</label>
                  <Dropdown
                    placeholder="Select alignment"
                    selection
                    options={toDropdown(options.alignments)}
                    value={form.alignment}
                    onChange={handleChange('alignment')}
                  />
                </Form.Field>
              </>
            )}

            {activeStep === 4 && (
              <>
                <Header as="h3">Stats</Header>
                <p>Point buy: start at 8 (FRC/LCK excluded) with 27 points to spend.</p>
                <div className="portal-highlight">Points remaining: {pointsRemaining}</div>
                <Table celled compact style={{ marginTop: '1rem' }}>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Stat</Table.HeaderCell>
                      <Table.HeaderCell>Value</Table.HeaderCell>
                      <Table.HeaderCell>Adjust</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {statKeys.map((key) => (
                      <Table.Row key={key}>
                        <Table.Cell>{statLabels[key]}</Table.Cell>
                        <Table.Cell>{stats[key]}</Table.Cell>
                        <Table.Cell>
                          <Button
                            size="tiny"
                            type="button"
                            onClick={() => adjustStat(key, -1)}
                            disabled={stats[key] <= statMin}
                          >
                            -
                          </Button>
                          <Button
                            size="tiny"
                            type="button"
                            onClick={() => adjustStat(key, 1)}
                            disabled={stats[key] >= statMax || pointsRemaining <= 0}
                          >
                            +
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>

                <Button
                  primary
                  type="button"
                  onClick={handleSubmit}
                  loading={submitting}
                  disabled={submitting}
                >
                  Create Character
                </Button>
              </>
            )}
                </Form>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
                  <Button
                    type="button"
                    onClick={() => setActiveStep((prev) => Math.max(prev - 1, 0))}
                    disabled={activeStep === 0}
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    primary
                    onClick={() => setActiveStep((prev) => Math.min(prev + 1, steps.length - 1))}
                    disabled={activeStep === steps.length - 1 || !canGoNext}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
          <aside className="portal-rail">
            <div className="lots-card">
              <div className="portal-kicker">Need help?</div>
              <h4>New to LotS</h4>
              <p>Pick a race that matches your story idea, then follow the suggested class combos.</p>
            </div>
            <div className="lots-card">
              <div className="portal-kicker">Veterans</div>
              <h4>Build fast</h4>
              <p>Use the stats panel to optimize for your preferred build, then jump straight to play.</p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default CharacterNew;
